// [claude-code 2026-04-18] Composer polish: (1) IME composition guard on Enter so submitting an
//   IME candidate with Enter no longer sends the message mid-composition; (2) Attach popup auto-
//   dismisses when the user starts typing; (3) Queue chips show "+N more" hint when > 2 jobs
//   are active (was silent truncation); (4) Compact mode bottom-bar padding bumped to match the
//   main composer so the send button no longer crowds the Harper pill; (5) Paste handler logs
//   a one-time note when non-image clipboard items are dropped (was silent).
// [claude-code 2026-03-28] S8-T7: reasoning affordance, no bg/border when active
// [claude-code 2026-03-11] T5: steer strip removed, queue chips added, RiskFlow drag-drop
// [claude-code 2026-03-22] Track 4: compact toolbox slots and reasoning selector
// Based on 21st.dev ChatGPT prompt input, rewritten without Radix
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type ClipboardEvent,
  type ReactNode,
} from "react";
import { X, Maximize2, Clock } from "lucide-react";
import { SolvysLoader } from "../shared/SolvysLoader";
import { FintheonSlashPicker } from "../chat/FintheonSlashPicker";
import {
  FintheonAttachPopup,
  type HeadlineAttachment,
} from "../chat/FintheonAttachPopup";
import { SkillBadge } from "../chat/SkillBadge";
import { UsageRing } from "../chat/UsageRing";
import { ReasoningLevelSelector } from "../chat/ReasoningLevelSelector";
import { ComposerSafetyNotice } from "../chat/ComposerSafetyNotice";
import type { ReasoningLevel } from "../chat/reasoning";
import {
  HeadlineChips,
  type HeadlineChip,
} from "../chat/HeadlinePickerPopover";
import { ContextMentionDrawer } from "../chat/ContextMentionDrawer";
import {
  RepoChatComposer,
  RepoChatComposerSurface,
} from "../chat/composer/RepoChatComposer";
import {
  ComposerAttachedItemsDrawer,
  type ComposerDocumentAttachment,
  type ComposerNarrativeAttachment,
} from "../chat/composer/ComposerAttachedItemsDrawer";
import {
  ComposerConnectorMark,
  ComposerPluginStackMark,
  ComposerRemoveMark,
  type ComposerConnectorItem,
} from "../chat/composer/composerConnectors";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { AgenticTaskMode } from "../../lib/agentic/task-mode";
import { AgenticModeToggle } from "../chat/agentic/AgenticModeToggle";
import {
  formatMentionContext,
  mentionToken,
  type ContextMention,
} from "../../lib/context-mentions";
import { useSettings } from "../../contexts/SettingsContext";
import {
  GlobalChatComposer,
  type GlobalComposerActivity,
} from "../chat/global-composer";
import type { AgentChatComposerHandle } from "../premium/agent-chat-input/composer";

/* ------------------------------------------------------------------ */
/*  RiskFlow preview builder                                          */
/* ------------------------------------------------------------------ */

function buildRiskFlowPreview(data: {
  headline?: string;
  summary?: string;
  ticker?: string;
  direction?: string;
  source?: string;
  ivScore?: number;
  publishedAt?: string;
}): string | null {
  if (!data.headline) return null;
  const parts: string[] = [];
  parts.push(`[RiskFlow Context]`);
  parts.push(`Headline: ${data.headline}`);
  if (data.source) parts.push(`Source: ${data.source}`);
  if (data.ivScore != null) parts.push(`IV Score: ${data.ivScore.toFixed(1)}`);
  if (data.ticker) parts.push(`Ticker: ${data.ticker}`);
  if (data.direction) parts.push(`Direction: ${data.direction}`);
  if (data.publishedAt) {
    const ago = Math.round(
      (Date.now() - new Date(data.publishedAt).getTime()) / 60000,
    );
    parts.push(`Time: ${ago}m ago`);
  }
  if (data.summary && data.summary !== data.headline) {
    parts.push(`Summary: ${data.summary}`);
  }
  return parts.join("\n");
}

function getMentionQuery(value: string): string | null {
  const match = value.match(/(^|\s)@([a-zA-Z0-9_.-]{0,48})$/);
  return match ? match[2] : null;
}

function replaceMentionQuery(value: string, item: ContextMention): string {
  const token = mentionToken(item);
  if (/(^|\s)@[a-zA-Z0-9_.-]{0,48}$/.test(value)) {
    return value.replace(/(^|\s)@[a-zA-Z0-9_.-]{0,48}$/, `$1${token} `);
  }
  return `${value}${value.endsWith(" ") || value.length === 0 ? "" : " "}${token} `;
}

function ComposerDrawerAccordion({
  sections,
  expandedId,
  onExpandedIdChange,
}: {
  sections: Array<{ id: string; label: string; content: ReactNode }>;
  expandedId: string | null;
  onExpandedIdChange: (id: string | null) => void;
}) {
  const activeId = expandedId ?? sections[0]?.id ?? null;
  return (
    <RepoChatComposerSurface
      role="region"
      aria-label="Composer drawer"
      open={sections.length > 0}
      kind="drawer"
      className="fintheon-drawer-accordion"
      maxHeight="420px"
    >
      {sections.map((section) => {
        const open = section.id === activeId;
        return (
          <section
            key={section.id}
            className="fintheon-drawer-accordion__section"
            data-open={open ? "true" : "false"}
          >
            <button
              type="button"
              className="fintheon-drawer-accordion__trigger"
              onClick={() => onExpandedIdChange(open ? null : section.id)}
            >
              <span>{section.label}</span>
              <span aria-hidden="true">{open ? "-" : "+"}</span>
            </button>
            {open ? (
              <div className="fintheon-drawer-accordion__body">
                {section.content}
              </div>
            ) : null}
          </section>
        );
      })}
    </RepoChatComposerSurface>
  );
}

/* ------------------------------------------------------------------ */
/*  Props                                                             */
/* ------------------------------------------------------------------ */

export interface PromptBoxProps {
  onSend: (message: string, images?: string[]) => void;
  onSteerDraft?: (message: string, images?: string[]) => void;
  onStop?: () => void;
  value?: string;
  onValueChange?: (value: string) => void;
  canSend?: (message: string, images: string[]) => boolean;
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  isProcessing?: boolean;
  placeholder?: string;
  thinkHarder: boolean;
  setThinkHarder: (v: boolean) => void;
  reasoningLevel?: ReasoningLevel;
  onReasoningLevelChange?: (value: ReasoningLevel) => void;
  activeSkill: string | null;
  onSelectSkill: (id: string | null) => void;
  showSkills: boolean;
  onToggleSkills: () => void;
  onSlashSelect?: (skillId: string) => void;
  disabled?: boolean;
  draftKey?: string;
  compact?: boolean;
  showSafetyNotice?: boolean;
  lastError?: string | null;
  showLastError?: boolean;
  disabledSkills?: Record<string, { reason: string }>;
  // Voice
  voiceEnabled?: boolean;
  voiceState?: string;
  onToggleVoice?: () => void;
  activityOverride?: GlobalComposerActivity;
  activityLabel?: string;
  onResume?: () => void;
  // Queue chips
  queueJobs?: Array<{ jobId: string; status: string; position: number }>;
  onCancelJob?: (jobId: string) => void;
  queueCount?: number;
  contextStats?: {
    messageCount: number;
    estimatedTokens: number;
    connectorCount: number;
    activeSkillLabel?: string | null;
  };
  agenticMode?: AgenticTaskMode;
  onAgenticModeChange?: (mode: AgenticTaskMode) => void;
  // Slots for compact toolbar controls
  personaSlot?: ReactNode;
  toolsSlot?: ReactNode;
  providerSlot?: ReactNode;
  workspaceSlot?: ReactNode;
  leftToolbarSlot?: ReactNode;
  // S60-T3: Modal-aware toolbox triggers (composer toolbar)
  pluginSlot?: ReactNode;
  mcpSlot?: ReactNode;
  toolboxDrawerSlot?: ReactNode;
  toolboxOpen?: boolean;
  onInputActivity?: () => void;
  // Relay dispatch button (leftmost in action cluster). Renders either relay or disconnect.
  relaySlot?: ReactNode;
  // Optional banner shown above the input while dispatched (e.g. "Chatting on iPhone").
  dispatchBanner?: ReactNode;
  // Todo + Queue drawer toggle button
  todoSlot?: ReactNode;
  approvalDrawerSlot?: ReactNode;
  approvalDrawerOpen?: boolean;
  workDrawerSlot?: ReactNode;
  workDrawerOpen?: boolean;
  drawerPeekSlot?: ReactNode;
  draftCardsSlot?: ReactNode;
  // Boardroom: swap pulsing icon for newspaper RiskFlow picker
  onRiskFlowPick?: () => void;
  // Hide the reasoning selector where deep research is always on.
  hideThinkHarder?: boolean;
  // Headline attachment (multi-select from scored feed items)
  showAttachSelector?: boolean;
  attachSelectorTitle?: string;
  attachInitialTab?: "docs" | "media" | "riskflow";
  attachOpen?: boolean;
  onAttachOpenChange?: (open: boolean) => void;
  headlineAlerts?: RiskFlowAlert[];
  headlineChips?: HeadlineChip[];
  onHeadlineToggle?: (chip: HeadlineChip) => void;
  onHeadlineClear?: () => void;
  connectorItems?: ComposerConnectorItem[];
  attachedConnectorIds?: string[];
  onAttachConnector?: (id: string) => void;
  attachedNarratives?: ComposerNarrativeAttachment[];
  onAttachedNarrativeRemove?: (id: string) => void;
  onFocusChange?: (focused: boolean) => void;
  // S38-T1: History navigation
  recallText?: string | null;
  onRecallConsumed?: () => void;
  onHistoryUp?: () => void;
  onHistoryDown?: () => void;
  onHistoryEscape?: () => void;
  enterCreatesNewline?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function PromptBox({
  onSend,
  onSteerDraft,
  onStop,
  value,
  onValueChange,
  canSend,
  onPaste: externalOnPaste,
  isProcessing = false,
  placeholder = "Message your analysts...",
  thinkHarder,
  setThinkHarder,
  reasoningLevel = thinkHarder ? "deep" : "standard",
  onReasoningLevelChange,
  activeSkill,
  onSelectSkill,
  showSkills,
  onToggleSkills,
  disabled = false,
  draftKey = "fintheon:draft-analysis",
  compact = false,
  showSafetyNotice = true,
  lastError,
  showLastError = true,
  disabledSkills,
  voiceEnabled,
  voiceState,
  onToggleVoice,
  activityOverride,
  activityLabel,
  onResume,
  queueJobs,
  onCancelJob,
  queueCount = 0,
  contextStats,
  agenticMode = "plan",
  onAgenticModeChange,
  personaSlot,
  toolsSlot,
  providerSlot,
  workspaceSlot,
  leftToolbarSlot,
  pluginSlot,
  mcpSlot,
  toolboxDrawerSlot,
  toolboxOpen = false,
  onInputActivity,
  relaySlot,
  dispatchBanner,
  onRiskFlowPick,
  showAttachSelector = true,
  attachSelectorTitle = "Attach",
  attachInitialTab = "media",
  attachOpen,
  onAttachOpenChange,
  headlineAlerts,
  headlineChips,
  onHeadlineToggle,
  connectorItems = [],
  attachedConnectorIds = [],
  onAttachConnector,
  attachedNarratives = [],
  onAttachedNarrativeRemove,
  onFocusChange,
  hideThinkHarder,
  recallText,
  onRecallConsumed,
  onHistoryUp,
  onHistoryDown,
  onHistoryEscape,
  enterCreatesNewline = false,
  todoSlot,
  approvalDrawerSlot,
  approvalDrawerOpen = false,
  workDrawerSlot,
  workDrawerOpen = false,
  drawerPeekSlot,
  draftCardsSlot,
}: PromptBoxProps) {
  const { chatPermissionLevel, setChatPermissionLevel } = useSettings();
  const [text, setText] = useState("");
  const isControlled = value !== undefined;
  const draftText = value ?? text;
  const setDraftText = useCallback(
    (next: string | ((current: string) => string)) => {
      const resolved = typeof next === "function" ? next(draftText) : next;
      if (!isControlled) setText(resolved);
      onValueChange?.(resolved);
    },
    [draftText, isControlled, onValueChange],
  );
  const [images, setImages] = useState<string[]>([]);
  const [attachedDocuments, setAttachedDocuments] = useState<
    ComposerDocumentAttachment[]
  >([]);
  const [showAttach, setShowAttach] = useState(false);
  const isAttachOpen = attachOpen ?? showAttach;
  const setAttachOpen = useCallback(
    (open: boolean) => {
      if (attachOpen === open) return;
      if (attachOpen === undefined) {
        setShowAttach((current) => (current === open ? current : open));
      }
      onAttachOpenChange?.(open);
    },
    [attachOpen, onAttachOpenChange],
  );
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [selectedMentions, setSelectedMentions] = useState<ContextMention[]>(
    [],
  );
  const [fullSizeImage, setFullSizeImage] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [expandedDrawerId, setExpandedDrawerId] = useState<string | null>(null);
  const shouldShowWorkDrawerSlot = !!workDrawerSlot && workDrawerOpen;
  // IME composition state — blocks Enter-to-send while a candidate is being composed.
  const isComposingRef = useRef(false);
  const composerFocusedRef = useRef(false);
  const composerRef = useRef<AgentChatComposerHandle>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (toolboxOpen && !isAttachOpen) setAttachOpen(true);
  }, [isAttachOpen, setAttachOpen, toolboxOpen]);

  useEffect(() => {
    if (approvalDrawerOpen && isAttachOpen) setAttachOpen(false);
  }, [approvalDrawerOpen, isAttachOpen, setAttachOpen]);

  useEffect(() => {
    if (!showAttachSelector && isAttachOpen) setAttachOpen(false);
  }, [isAttachOpen, setAttachOpen, showAttachSelector]);

  /* Draft persistence — load on mount */
  useEffect(() => {
    if (isControlled) return;
    const draft = localStorage.getItem(draftKey);
    if (draft) setText(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Draft persistence — save on change */
  useEffect(() => {
    if (isControlled) return;
    if (draftText) {
      localStorage.setItem(draftKey, draftText);
    } else {
      localStorage.removeItem(draftKey);
    }
  }, [draftText, draftKey, isControlled]);

  /* S38-T1: Sync recalled history text into textarea */
  useEffect(() => {
    if (recallText !== null && recallText !== undefined) {
      setDraftText(recallText);
      onRecallConsumed?.();
    }
  }, [onRecallConsumed, recallText, setDraftText]);

  /* Full-size image dialog */
  useEffect(() => {
    if (fullSizeImage) {
      dialogRef.current?.showModal();
    }
  }, [fullSizeImage]);

  const setComposerFocused = useCallback(
    (next: boolean) => {
      composerFocusedRef.current = next;
      setFocused(next);
      onFocusChange?.(next);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("fintheon:chat-composer-focus", {
            detail: { focused: next },
          }),
        );
      }
    },
    [onFocusChange],
  );

  useEffect(() => {
    return () => {
      if (!composerFocusedRef.current) return;
      composerFocusedRef.current = false;
      onFocusChange?.(false);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("fintheon:chat-composer-focus", {
            detail: { focused: false },
          }),
        );
      }
    };
  }, [onFocusChange]);

  /* Send with vanish animation */
  const submitDraft = useCallback(
    (submit: typeof onSend) => {
      const nextDraftText = composerRef.current?.serialize().text ?? draftText;
      const msg = nextDraftText.trim();
      const documentContext = attachedDocuments
        .map((item) => `[Attached Document: ${item.filename}]\n${item.text}`)
        .join("\n\n");
      const hasComposerPayload =
        !!msg ||
        images.length > 0 ||
        attachedDocuments.length > 0 ||
        (headlineChips?.length ?? 0) > 0 ||
        attachedNarratives.length > 0;
      if (!hasComposerPayload) return;
      if (canSend && !canSend(msg, images)) return;

      submit(
        `${[msg, documentContext].filter(Boolean).join("\n\n")}${formatMentionContext(selectedMentions)}`,
        images.length > 0 ? images : undefined,
      );
      setDraftText("");
      setImages([]);
      setAttachedDocuments([]);
      setMentionQuery(null);
      setSelectedMentions([]);
      localStorage.removeItem(draftKey);
      composerRef.current?.clear();
    },
    [
      canSend,
      attachedDocuments,
      attachedNarratives.length,
      draftKey,
      draftText,
      headlineChips?.length,
      images,
      selectedMentions,
      setDraftText,
    ],
  );

  const handleSend = useCallback(() => {
    submitDraft(onSend);
  }, [onSend, submitDraft]);

  const handleSteerDraft = useCallback(() => {
    submitDraft(onSteerDraft ?? onSend);
  }, [onSend, onSteerDraft, submitDraft]);

  /* Keyboard shortcuts */
  const lastSpaceRef = useRef(0);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      // IME composition: Enter commits a candidate — don't send the message.
      // (Chromium also sets e.keyCode === 229 during composition; we keep a ref for safety.)
      if (isComposingRef.current || e.nativeEvent.isComposing) return;
      e.preventDefault();
      if (e.shiftKey) {
        handleSteerDraft();
        return;
      }
      if (isProcessing && !draftText.trim() && images.length === 0 && onStop) {
        onStop();
      } else {
        handleSend();
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !enterCreatesNewline) {
      if (isComposingRef.current || e.nativeEvent.isComposing) return;
      e.preventDefault();
      if (isProcessing && !draftText.trim() && images.length === 0 && onStop) {
        onStop();
      } else {
        handleSend();
      }
      return;
    }
    if (e.key === " " && isProcessing && onStop) {
      const now = Date.now();
      if (now - lastSpaceRef.current < 400) {
        e.preventDefault();
        onStop();
      }
      lastSpaceRef.current = now;
    }
    // S38-T1: Arrow history navigation
    if (e.key === "ArrowUp" && !draftText.trim() && onHistoryUp) {
      e.preventDefault();
      onHistoryUp();
    }
    if (e.key === "ArrowDown" && onHistoryDown) {
      e.preventDefault();
      onHistoryDown();
    }
    if (e.key === "Escape" && onHistoryEscape) {
      e.preventDefault();
      onHistoryEscape();
    }
  };

  /* Paste image support */
  const pasteWarnedRef = useRef(false);
  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    externalOnPaste?.(e as unknown as ClipboardEvent<HTMLTextAreaElement>);
    const items = e.clipboardData?.items;
    if (!items) return;
    let handledImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith("image/")) {
        handledImage = true;
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === "string") {
              setImages((prev) => [...prev, reader.result as string]);
            }
          };
          reader.readAsDataURL(blob);
        }
      }
    }
    if (!handledImage && items.length > 0 && !pasteWarnedRef.current) {
      // One-time log so we're not silent about dropped non-image clipboard payloads
      // (files, html fragments, etc). Most such pastes land as text via the default path.
      const nonImageTypes = Array.from(items)
        .map((it) => it.type || "<unknown>")
        .join(", ");
      console.debug(
        "[PromptBox] Non-image clipboard item(s) ignored by image handler:",
        nonImageTypes,
      );
      pasteWarnedRef.current = true;
    }
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAttachImage = useCallback((dataUrl: string) => {
    setImages((prev) => [...prev, dataUrl]);
  }, []);

  const handleSlashSelect = useCallback(
    (skillId: string) => {
      onSelectSkill(skillId);
      setSlashQuery(null);
      setDraftText("");
    },
    [onSelectSkill, setDraftText],
  );

  const handleComposerValueChange = useCallback(
    (next: string) => {
      setDraftText(next);
      setMentionQuery(getMentionQuery(next));
      if (next.length > 0) {
        if (isAttachOpen) setAttachOpen(false);
        onInputActivity?.();
      }
      if (next.startsWith("/") && !next.includes(" ") && !next.includes("\n")) {
        setSlashQuery(next.slice(1));
      } else {
        setSlashQuery(null);
      }
    },
    [isAttachOpen, onInputActivity, setAttachOpen, setDraftText],
  );

  const micListening = voiceState === "listening";

  /* RiskFlow drag-drop */
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const json = e.dataTransfer.getData("application/x-riskflow");
      if (!json) return;
      try {
        const data = JSON.parse(json) as {
          headline?: string;
          summary?: string;
          ticker?: string;
          direction?: string;
          source?: string;
          ivScore?: number;
          publishedAt?: string;
        };
        const preview = buildRiskFlowPreview(data);
        if (preview) {
          setDraftText((prev) => (prev ? `${prev}\n\n${preview}` : preview));
        }
      } catch {
        // Not valid riskflow data — ignore
      }
    },
    [setDraftText],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("application/x-riskflow")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const activeQueueJobs = (queueJobs ?? []).filter((j) => j.status !== "done");
  const visibleQueueJobs = activeQueueJobs.slice(0, 2);
  const hiddenQueueCount = Math.max(0, activeQueueJobs.length - 2);
  const attachDrawerSlot = showAttachSelector ? (
    <FintheonAttachPopup
      open={isAttachOpen}
      onClose={() => setAttachOpen(false)}
      initialTab={attachInitialTab}
      connectorItems={connectorItems}
      attachedConnectorIds={attachedConnectorIds}
      onAttachConnector={onAttachConnector}
      onAttachImage={handleAttachImage}
      onAttachDocument={({ filename, text }) => {
        setAttachedDocuments((current) => [
          ...current,
          {
            id: `${filename}-${Date.now().toString(36)}`,
            filename,
            text,
          },
        ]);
        setAttachOpen(false);
      }}
      riskflowAlerts={headlineAlerts}
      onAttachHeadlines={(items: HeadlineAttachment[]) => {
        if (onHeadlineToggle) {
          items.forEach((item) =>
            onHeadlineToggle({
              id: item.id,
              headline: item.headline,
              severity: item.severity,
              direction: item.direction,
            }),
          );
        }
      }}
    />
  ) : null;
  const mentionDrawerSlot = (
    <ContextMentionDrawer
      open={mentionQuery !== null}
      query={mentionQuery ?? ""}
      selected={selectedMentions}
      onClose={() => setMentionQuery(null)}
      onSelect={(item) => {
        setSelectedMentions((current) =>
          current.some((mention) => mention.id === item.id)
            ? current
            : [...current, item],
        );
        setDraftText((current) => replaceMentionQuery(current, item));
        setMentionQuery(null);
      }}
    />
  );
  const slashDrawerSlot = (
    <RepoChatComposerSurface
      role="region"
      aria-label="Slash command drawer"
      open={slashQuery !== null}
      kind="drawer"
      maxHeight="240px"
    >
      <FintheonSlashPicker
        query={slashQuery ?? ""}
        onSelect={handleSlashSelect}
        onDismiss={() => setSlashQuery(null)}
        onStop={onStop}
        disabledSkills={disabledSkills}
      />
    </RepoChatComposerSurface>
  );
  const drawerSections: Array<{
    id: string;
    label: string;
    content: ReactNode;
  }> = [];
  if (slashQuery !== null) {
    drawerSections.push({
      id: "slash",
      label: "Commands",
      content: slashDrawerSlot,
    });
  }
  if (mentionQuery !== null) {
    drawerSections.push({
      id: "mentions",
      label: "Mentions",
      content: mentionDrawerSlot,
    });
  }
  if (toolboxOpen && toolboxDrawerSlot) {
    drawerSections.push({
      id: "skills-connectors",
      label: "Plugins and connectors",
      content: toolboxDrawerSlot,
    });
  }
  if (showAttachSelector && isAttachOpen && attachDrawerSlot) {
    drawerSections.push({
      id: "attach",
      label: attachSelectorTitle,
      content: (
        <div className="grid min-h-0 gap-2">
          {pluginSlot || mcpSlot ? (
            <div
              className="flex min-h-9 items-center gap-1 px-2"
              aria-label="Plugin drawers"
            >
              {pluginSlot}
              {mcpSlot}
            </div>
          ) : null}
          {attachDrawerSlot}
        </div>
      ),
    });
  }
  if (shouldShowWorkDrawerSlot && workDrawerSlot) {
    drawerSections.push({
      id: "work",
      label: "Tasks",
      content: workDrawerSlot,
    });
  }
  const activeDrawer = drawerSections[0]?.id ?? null;
  const isSingleWorkDrawer =
    drawerSections.length === 1 && drawerSections[0]?.id === "work";
  const drawerBodySlot = isSingleWorkDrawer ? (
    drawerSections[0].content
  ) : drawerSections.length > 0 ? (
    <ComposerDrawerAccordion
      sections={drawerSections}
      expandedId={expandedDrawerId}
      onExpandedIdChange={setExpandedDrawerId}
    />
  ) : null;
  const shouldFloatPeekAboveDrawer = Boolean(drawerPeekSlot && drawerBodySlot);
  const drawerLayerSlot = shouldFloatPeekAboveDrawer ? (
    <>
      {drawerPeekSlot}
      {drawerBodySlot}
    </>
  ) : drawerBodySlot ? (
    drawerBodySlot
  ) : (
    drawerPeekSlot
  );

  useEffect(() => {
    if (drawerSections.length === 0) {
      if (expandedDrawerId !== null) setExpandedDrawerId(null);
      return;
    }
    if (
      expandedDrawerId &&
      drawerSections.some((section) => section.id === expandedDrawerId)
    ) {
      return;
    }
    setExpandedDrawerId(drawerSections[0].id);
  }, [drawerSections, expandedDrawerId]);
  const attachedConnectorSet = new Set(attachedConnectorIds);
  const attachedConnectorItems = connectorItems.filter((item) =>
    attachedConnectorSet.has(item.id),
  );
  const attachedConnectorCount = attachedConnectorItems.length;
  const hasAttachedBottomItems =
    attachedDocuments.length > 0 ||
    images.length > 0 ||
    (headlineChips?.length ?? 0) > 0 ||
    attachedNarratives.length > 0;
  const hasComposerPayload =
    !!draftText.trim() ||
    images.length > 0 ||
    attachedDocuments.length > 0 ||
    (headlineChips?.length ?? 0) > 0 ||
    attachedNarratives.length > 0;
  const composerActivity: GlobalComposerActivity =
    activityOverride ??
    (approvalDrawerOpen
      ? "approval"
      : micListening
        ? "voice"
        : isProcessing
          ? "running"
          : queueCount > 0
            ? "queued"
            : "idle");

  return (
    <>
      <RepoChatComposer
        format={compact ? "compact" : "full"}
        className="px-0 pb-4 pt-3"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {/* Active skill badge */}
        {activeSkill && (
          <div className="mb-2">
            <SkillBadge
              skillId={activeSkill}
              onDismiss={() => onSelectSkill(null)}
            />
          </div>
        )}

        {/* Error banner */}
        {showLastError && lastError && (
          <div className="mb-2 max-h-24 min-w-0 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {lastError}
          </div>
        )}

        {drawerLayerSlot && (
          <div
            className="fintheon-chat-input-drawer-layer"
            data-active-drawer={activeDrawer ?? "peek"}
            data-floating-peek={shouldFloatPeekAboveDrawer ? "true" : "false"}
          >
            {drawerLayerSlot}
          </div>
        )}

        {draftCardsSlot}

        {/* Queue chips (max 2 + overflow hint) */}
        {visibleQueueJobs.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2">
            {visibleQueueJobs.map((job) => (
              <span
                key={job.jobId}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[var(--fintheon-accent)]/20 bg-[#0d0c09]/80 text-[11px] text-[#f0ead6]/60"
              >
                {job.status === "processing" ? (
                  <SolvysLoader size={10} />
                ) : (
                  <Clock size={10} className="text-zinc-600" />
                )}
                <span>
                  {job.status === "processing"
                    ? "Running"
                    : `Queue #${job.position}`}
                </span>
                {onCancelJob && (
                  <button
                    onClick={() => onCancelJob(job.jobId)}
                    className="ml-0.5 text-zinc-600 hover:text-red-400 transition-colors"
                    title="Cancel"
                  >
                    <X size={10} />
                  </button>
                )}
              </span>
            ))}
            {hiddenQueueCount > 0 && (
              <span
                className="inline-flex items-center px-2 py-1 rounded border border-[var(--fintheon-accent)]/10 bg-[#0d0c09]/60 text-[10px] text-[#f0ead6]/40"
                title={`${hiddenQueueCount} more job(s) queued`}
              >
                +{hiddenQueueCount} more
              </span>
            )}
          </div>
        )}

        {/* Drag-over indicator */}
        {dragOver && (
          <div className="mb-2 rounded-xl border-2 border-dashed border-[var(--fintheon-accent)]/40 bg-[var(--fintheon-accent)]/5 px-4 py-3 text-center text-[12px] text-[var(--fintheon-accent)]/70">
            Drop RiskFlow alert here
          </div>
        )}

        {/* Dispatch banner — shown above the input while a mirror session is active */}
        {dispatchBanner}

        <GlobalChatComposer
          composerRef={composerRef}
          compact={compact}
          placeholder={placeholder}
          showAttach={showAttachSelector}
          attachOpen={isAttachOpen}
          onAttachToggle={() => {
            const nextOpen = !isAttachOpen;
            if (nextOpen) onInputActivity?.();
            setAttachOpen(nextOpen);
          }}
          voiceEnabled={voiceEnabled}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocusChange={setComposerFocused}
          className="fintheon-composer-input"
          controller={{
            value: draftText,
            disabled,
            canSubmit: hasComposerPayload,
            activity: composerActivity,
            activityLabel,
            queueCount,
            onValueChange: handleComposerValueChange,
            onSubmit: handleSend,
            onStop,
            onResume,
            onToggleVoice,
          }}
          approval={
            approvalDrawerOpen && approvalDrawerSlot ? (
              <div className="fintheon-chat-approval-overlay-stage">
                {approvalDrawerSlot}
              </div>
            ) : null
          }
          leadingActions={
            <>
              {relaySlot}
              {leftToolbarSlot}
              {onAgenticModeChange ? (
                <AgenticModeToggle
                  value={agenticMode}
                  onChange={onAgenticModeChange}
                  compact={compact}
                  permissionLevel={chatPermissionLevel}
                  onPermissionLevelChange={setChatPermissionLevel}
                />
              ) : null}
              {attachedConnectorCount > 0 ? (
                <button
                  type="button"
                  className="fintheon-composer-plugin-stack-button"
                  title={`${attachedConnectorCount} attached plugins`}
                  aria-label={`${attachedConnectorCount} attached plugins`}
                  onClick={() => {
                    onInputActivity?.();
                    setAttachOpen(true);
                  }}
                >
                  <span
                    className="fintheon-composer-plugin-stack-icon"
                    aria-hidden="true"
                  >
                    <ComposerPluginStackMark />
                  </span>
                  <span className="fintheon-composer-plugin-stack-badge">
                    +{attachedConnectorCount}
                  </span>
                </button>
              ) : null}
              {attachedConnectorItems.map((connector) => (
                <button
                  key={connector.id}
                  type="button"
                  className="fintheon-composer-connector-button is-active"
                  title={`Remove ${connector.label}`}
                  aria-label={`Remove ${connector.label}`}
                  onClick={() => onAttachConnector?.(connector.id)}
                >
                  <span
                    className="fintheon-composer-connector-icon"
                    aria-hidden="true"
                  >
                    <ComposerConnectorMark
                      kind={connector.kind}
                      className="icon-mark"
                    />
                    <ComposerRemoveMark className="icon-remove" />
                  </span>
                  <span>{connector.shortLabel}</span>
                </button>
              ))}
              {workspaceSlot}
              {toolsSlot}
              {todoSlot}
            </>
          }
          trailingActions={
            <>
              {providerSlot}
              {!hideThinkHarder ? (
                <ReasoningLevelSelector
                  value={reasoningLevel}
                  onChange={(next) => {
                    onReasoningLevelChange?.(next);
                    setThinkHarder(next === "deep" || next === "extra-high");
                  }}
                  compact={compact}
                />
              ) : null}
              {personaSlot}
              <UsageRing
                stats={contextStats}
                draftText={draftText}
                queuedCount={queueCount}
              />
            </>
          }
        />
        <ComposerAttachedItemsDrawer
          documents={attachedDocuments}
          imageCount={images.length}
          headlines={headlineChips}
          narratives={attachedNarratives}
          onRemoveDocument={(id) =>
            setAttachedDocuments((current) =>
              current.filter((item) => item.id !== id),
            )
          }
          onRemoveImage={removeImage}
          onRemoveHeadline={(id) => {
            const chip = headlineChips?.find((item) => item.id === id);
            if (chip) onHeadlineToggle?.(chip);
          }}
          onRemoveNarrative={onAttachedNarrativeRemove}
        />
        {showSafetyNotice && !focused ? (
          <ComposerSafetyNotice compact={compact} />
        ) : null}
      </RepoChatComposer>

      {/* Full-size image dialog (native <dialog>) */}
      <dialog
        ref={dialogRef}
        onClick={(e) => {
          if (e.target === dialogRef.current) {
            dialogRef.current?.close();
            setFullSizeImage(null);
          }
        }}
        onClose={() => setFullSizeImage(null)}
        className="bg-transparent backdrop:bg-black/80 max-w-[90vw] max-h-[90vh] p-0 rounded-xl"
      >
        {fullSizeImage && (
          <div className="relative">
            <img
              src={fullSizeImage}
              alt=""
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-xl"
            />
            <button
              onClick={() => {
                dialogRef.current?.close();
                setFullSizeImage(null);
              }}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <X size={16} />
            </button>
            <button
              onClick={() => window.open(fullSizeImage, "_blank")}
              className="absolute top-2 right-12 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
              title="Open in new tab"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        )}
      </dialog>
    </>
  );
}
